import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Group,
  Stack,
  Text,
  Select,
  Autocomplete,
  TextInput,
  Card,
  Alert,
  Code,
  ActionIcon,
  Tooltip,
  Badge,
  Divider,
} from '@mantine/core';
import {
  IconPlus,
  IconTrash,
  IconAlertCircle,
  IconTemplate,
} from '@tabler/icons-react';

interface ValidationRule {
  expr: string;
  message?: string;
  level?: 'error' | 'warning';
}

interface ValidationTemplate {
  expr: string;
  async?: boolean;
  params?: string[];
}

interface TemplateValidationEditorProps {
  validationRules?: ValidationRule | ValidationRule[];
  onValidationChange: (rules: ValidationRule | ValidationRule[] | undefined) => void;
  templates?: Record<string, ValidationTemplate>;
  fieldName?: string;
  recordFields?: Array<{ xmlTag: string; type: string; description?: string }>;
  allRecordFields?: Array<{ xmlTag: string; type: string; description?: string; recordName?: string }>;
}

interface TemplateApplication {
  templateName: string;
  paramMappings: Record<string, string>;
  message: string;
  level: 'error' | 'warning';
}

export const TemplateValidationEditor: React.FC<TemplateValidationEditorProps> = ({
  validationRules,
  onValidationChange,
  templates = {},
  fieldName = '',
  recordFields = [],
  allRecordFields = [],
}) => {
  const [rules, setRules] = useState<ValidationRule[]>([]);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [paramMappings, setParamMappings] = useState<Record<string, string>>({});
  const [templateMessage, setTemplateMessage] = useState('');
  const [templateLevel, setTemplateLevel] = useState<'error' | 'warning'>('error');

  // Initialize rules from props
  const syncingFromPropsRef = useRef(false);

  useEffect(() => {
    syncingFromPropsRef.current = true;
    if (!validationRules) {
      setRules([]);
    } else if (Array.isArray(validationRules)) {
      setRules(validationRules);
    } else {
      setRules([validationRules]);
    }
  }, [validationRules]);

  // Update parent when rules change
  useEffect(() => {
    if (syncingFromPropsRef.current) {
      syncingFromPropsRef.current = false;
      return;
    }
    if (rules.length === 0) {
      onValidationChange(undefined);
    } else if (rules.length === 1) {
      onValidationChange(rules[0]);
    } else {
      onValidationChange(rules);
    }
  }, [rules, onValidationChange]);

  const handleAddCustomRule = useCallback(() => {
    const newRule: ValidationRule = {
      expr: 'value > 0', // Default expression
      message: `${fieldName} validation failed`,
      level: 'error',
    };
    setRules(prev => [...prev, newRule]);
  }, [fieldName]);

  const handleUpdateRule = useCallback((index: number, updatedRule: ValidationRule) => {
    setRules(prev => prev.map((rule, i) => i === index ? updatedRule : rule));
  }, []);

  const handleRemoveRule = useCallback((index: number) => {
    setRules(prev => prev.filter((_, i) => i !== index));
  }, []);

  const getAvailableParameters = useCallback(() => {
    const params = new Set<string>();

    // Always available
    params.add('value');
    params.add('Value');

    // Current record fields
    recordFields.forEach(field => {
      params.add(field.xmlTag);
    });

    // All record fields for cross-record references
    allRecordFields.forEach(field => {
      if (field.recordName) {
        params.add(`${field.recordName}.${field.xmlTag}`);
      } else {
        params.add(field.xmlTag);
      }
    });

    return Array.from(params);
  }, [recordFields, allRecordFields]);

  const handleApplyTemplate = useCallback(() => {
    if (!selectedTemplate || !templates[selectedTemplate]) return;

    const template = templates[selectedTemplate];

    // Build the template call expression
    const paramCalls = Object.entries(paramMappings)
      .filter(([_, value]) => value.trim())
      .map(([param, value]) => `${param}=${value}`)
      .join(', ');

    const expr = `@${selectedTemplate}(${paramCalls})`;

    const newRule: ValidationRule = {
      expr,
      message: templateMessage || `${fieldName} failed ${selectedTemplate} validation`,
      level: templateLevel,
    };

    setRules(prev => [...prev, newRule]);

    // Reset template builder
    setSelectedTemplate('');
    setParamMappings({});
    setTemplateMessage('');
    setTemplateLevel('error');
    setShowTemplateBuilder(false);
  }, [selectedTemplate, templates, paramMappings, templateMessage, templateLevel, fieldName]);

  const parseTemplateCall = useCallback((expr: string) => {
    const match = expr.match(/^@(\w+)\((.*)\)$/);
    if (!match) return null;

    const [, templateName, paramsStr] = match;
    const params: Record<string, string> = {};

    if (paramsStr) {
      const paramPairs = paramsStr.split(',');
      for (const pair of paramPairs) {
        const [key, value] = pair.split('=').map(s => s.trim());
        if (key && value) {
          params[key] = value;
        }
      }
    }

    return { templateName, params };
  }, []);

  const availableParameters = getAvailableParameters();
  const templateOptions = Object.keys(templates).map(name => ({
    value: name,
    label: `${name} ${templates[name].async ? '(async)' : ''}`,
  }));

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text fw={500} size="sm">Validation Rules</Text>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconTemplate size="0.8rem" />}
            onClick={() => setShowTemplateBuilder(!showTemplateBuilder)}
            disabled={Object.keys(templates).length === 0}
          >
            Add Template
          </Button>
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconPlus size="0.8rem" />}
            onClick={handleAddCustomRule}
          >
            Custom Rule
          </Button>
        </Group>
      </Group>

      {/* Template Builder */}
      {showTemplateBuilder && (
        <Card withBorder mb="md" p="sm">
          <Stack gap="sm">
            <Text fw={500} size="sm">Apply Validation Template</Text>

            <Select
              label="Template"
              placeholder="Choose a validation template"
              data={templateOptions}
              value={selectedTemplate}
              onChange={(value) => {
                setSelectedTemplate(value || '');
                if (value && templates[value]) {
                  // Initialize param mappings
                  const template = templates[value];
                  const mappings: Record<string, string> = {};
                  template.params?.forEach(param => {
                    // Smart defaults
                    if (param === 'value' || param === 'Value') {
                      mappings[param] = 'value';
                    } else if (recordFields.find(f => f.xmlTag === param)) {
                      mappings[param] = param;
                    } else {
                      mappings[param] = '';
                    }
                  });
                  setParamMappings(mappings);
                }
              }}
              searchable
            />

            {selectedTemplate && templates[selectedTemplate] && (
              <>
                <Box>
                  <Text size="xs" c="dimmed" mb="xs">Template Expression:</Text>
                  <Code block style={{ fontSize: '0.7rem' }}>
                    {templates[selectedTemplate].expr}
                  </Code>
                </Box>

                {templates[selectedTemplate].params && templates[selectedTemplate].params!.length > 0 && (
                  <Box>
                    <Text fw={500} size="sm" mb="xs">Parameter Mapping:</Text>
                    <Stack gap="xs">
                      {templates[selectedTemplate].params!.map(param => (
                        <Autocomplete
                          key={param}
                          label={param}
                          placeholder={`Map ${param} to a field or value`}
                          data={[
                            ...availableParameters,
                            // Common literal values
                            '1000000', '0', 'true', 'false',
                          ]}
                          value={paramMappings[param] || ''}
                          onChange={(value) => setParamMappings(prev => ({
                            ...prev,
                            [param]: value,
                          }))}
                          size="xs"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <Group grow>
                  <TextInput
                    label="Error Message"
                    placeholder="Custom validation message"
                    value={templateMessage}
                    onChange={(e) => setTemplateMessage(e.target.value)}
                    size="xs"
                  />
                  <Select
                    label="Level"
                    data={[
                      { value: 'error', label: 'Error' },
                      { value: 'warning', label: 'Warning' },
                    ]}
                    value={templateLevel}
                    onChange={(value) => setTemplateLevel(value as 'error' | 'warning')}
                    size="xs"
                  />
                </Group>

                <Group justify="flex-end">
                  <Button
                    size="xs"
                    onClick={handleApplyTemplate}
                    disabled={!selectedTemplate || (
                      templates[selectedTemplate].params?.some(param => !paramMappings[param]?.trim())
                    )}
                  >
                    Apply Template
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Card>
      )}

      {/* Current Rules */}
      {rules.length === 0 ? (
        <Alert color="gray" variant="light">
          <Text size="sm">No validation rules defined for this field.</Text>
        </Alert>
      ) : (
        <Stack gap="sm">
          {rules.map((rule, index) => {
            const templateCall = parseTemplateCall(rule.expr);

            return (
              <Card key={index} withBorder p="sm">
                <Group justify="space-between" align="flex-start">
                  <Stack gap="xs" style={{ flex: 1 }}>
                    {templateCall ? (
                      <Group gap="xs">
                        <Badge color="blue" size="sm">Template</Badge>
                        <Text fw={500} size="sm">{templateCall.templateName}</Text>
                        {templates[templateCall.templateName]?.async && (
                          <Badge color="violet" size="xs">Async</Badge>
                        )}
                      </Group>
                    ) : (
                      <Group gap="xs">
                        <Badge color="gray" size="sm">Custom</Badge>
                        <Text fw={500} size="sm">Expression</Text>
                      </Group>
                    )}

                    <Code block style={{ fontSize: '0.7rem' }}>
                      {rule.expr}
                    </Code>

                    <Group gap="xs" align="center">
                      <Text size="xs" c="dimmed">Message:</Text>
                      <Text size="xs">{rule.message || 'No message'}</Text>
                      <Badge color={rule.level === 'warning' ? 'yellow' : 'red'} size="xs">
                        {rule.level || 'error'}
                      </Badge>
                    </Group>
                  </Stack>

                  <Tooltip label="Remove rule">
                    <ActionIcon
                      color="red"
                      variant="light"
                      size="sm"
                      onClick={() => handleRemoveRule(index)}
                    >
                      <IconTrash size="0.8rem" />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}

      {Object.keys(templates).length === 0 && (
        <Alert color="blue" variant="light" mt="md" icon={<IconAlertCircle />}>
          <Text size="sm">
            No validation templates available. Create templates in the Validation Templates tab to use them here.
          </Text>
        </Alert>
      )}
    </Box>
  );
};
